import {fetchRepositoryFromServer, type RepositoryPickerRepository} from '@github-ui/issues-repository-picker'
import {fetchAccountIssueOrganizations} from '@github-ui/item-picker/fetch-org-repository-picker-items'
import {useEffect} from 'react'

import type {useIssueCreateConfigContext} from '../contexts/IssueCreateConfigContext'
import type {CreateIssueFragment} from './../create-issue-fragment-types'

type IssueCreateConfig = ReturnType<typeof useIssueCreateConfigContext>

type UseDefaultRepositorySelectionProps = {
  repository: RepositoryPickerRepository | undefined
  /** The current repository fragment, when one is already resolved for this surface. */
  data: CreateIssueFragment | null
  isLoadingRepositoryData: boolean
  topRepositories: RepositoryPickerRepository[] | null | 'unset'
  orgSelectionEnabled: boolean
  optionConfig: IssueCreateConfig['optionConfig']
  onRepositorySelected: (repository: RepositoryPickerRepository | undefined) => void
  setOrgRepoSelection: IssueCreateConfig['setOrgRepoSelection']
}

/**
 * Resolves the default repository to select when the surface opens without one already chosen.
 *
 * Order of preference:
 * 1. An already-resolved current repository (`data`).
 * 2. With org selection enabled, the enrolled organization's designated repository (resolved from
 *    the server), falling back to the top repository.
 * 3. With org selection disabled, the top repository when no scoped owner drives the picker.
 */
export function useDefaultRepositorySelection({
  repository,
  data,
  isLoadingRepositoryData,
  topRepositories,
  orgSelectionEnabled,
  optionConfig,
  onRepositorySelected,
  setOrgRepoSelection,
}: UseDefaultRepositorySelectionProps) {
  useEffect(() => {
    if (repository !== undefined) return

    if (data) {
      onRepositorySelected(data)
      return
    }

    if (isLoadingRepositoryData || topRepositories === 'unset' || !topRepositories) {
      return
    }

    // May be undefined when the viewer has no top repositories. The org-resolution path below does
    // not depend on it, so we still run to resolve the org's default repository from the server.
    const fallbackRepository = topRepositories[0]

    if (!orgSelectionEnabled) {
      // Preserve existing behavior: only auto-pick when no scoped owner drives the in-pane picker.
      if (fallbackRepository && !optionConfig.scopedRepositoryOwner) {
        onRepositorySelected(fallbackRepository)
      }
      return
    }

    // Flag on: prefer the current Planning account when it is an enrolled org, otherwise the first
    // enrolled org, otherwise fall back to the top repo. A user-scoped surface has no org to default to.
    const scope = optionConfig.scopedRepositoryOwner
    const orgScopeLogin = scope?.type === 'organization' ? scope.login : undefined
    let cancelled = false
    const abortController = new AbortController()
    void (async () => {
      let organization: Awaited<ReturnType<typeof fetchAccountIssueOrganizations>>[number] | undefined
      let orgRepository: RepositoryPickerRepository | undefined
      if (scope?.type !== 'user') {
        try {
          const organizations = await fetchAccountIssueOrganizations(orgScopeLogin)
          // The server already scopes to the requested owner, so take the first result either way
          // rather than re-matching on login (which is case-sensitive and can drop the scoped org).
          organization = organizations[0]
          if (organization) {
            orgRepository =
              (await fetchRepositoryFromServer(
                organization.repositoryOwner,
                organization.repositoryName,
                abortController.signal,
              )) ?? undefined
          }
        } catch {
          // Fall through to the top-repository fallback below (also catches the abort on cleanup).
        }
      }

      if (cancelled || repository !== undefined) return

      if (organization && orgRepository) {
        setOrgRepoSelection({kind: 'org', organization})
        onRepositorySelected(orgRepository)
      } else if (fallbackRepository) {
        onRepositorySelected(fallbackRepository)
      }
    })()

    return () => {
      cancelled = true
      abortController.abort()
    }
  }, [
    data,
    isLoadingRepositoryData,
    onRepositorySelected,
    optionConfig,
    repository,
    topRepositories,
    orgSelectionEnabled,
    setOrgRepoSelection,
  ])
}
